import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ArrowRight, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useEmpresaLayout } from '@/hooks/useEmpresaLayout';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);
  
  const { login, isAuthenticated, hasPermission } = useAuth();
  const { layoutConfig } = useEmpresaLayout();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      // Sempre tentar redirecionar para /dashboards primeiro
      if (hasPermission('Dashboard')) {
        navigate('/dashboards', { replace: true });
        return;
      }
      
      // Se não tem acesso ao Dashboard, redirecionar para análise de projetos
      navigate('/chamados/projecoes', { replace: true });
    }
  }, [isAuthenticated, navigate, hasPermission]);

  // Resetar erro da logo quando layoutConfig mudar
  useEffect(() => {
    setLogoError(false);
  }, [layoutConfig?.logo_url]);

  const isFormValid = email.trim() !== '' && password.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid) return;

    setIsLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast({
          title: "Login realizado com sucesso!",
          description: "Bem-vindo ao sistema.",
        });
        // Sempre tentar redirecionar para /dashboards primeiro
        if (hasPermission('Dashboard')) {
          navigate('/dashboards', { replace: true });
        } else {
          // Se não tem acesso ao Dashboard, redirecionar para análise de projetos
          navigate('/chamados/projecoes', { replace: true });
        }
      } else {
        toast({
          title: "Erro no login",
          description: result.message || "Usuário ou senha inválidos.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro no sistema",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // TODO: Implementar recuperação de senha
    toast({
      title: "Funcionalidade em desenvolvimento",
      description: "A recuperação de senha será implementada em breve.",
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <Card className="shadow-lg border border-gray-200">
          {/* Header */}
          <CardHeader className="bg-white border-b border-gray-200 pb-8">
            {/* Logo da empresa */}
            <div className="flex justify-center">
              <div className="bg-gray-50 p-4 rounded-xl min-h-[96px] flex items-center justify-center border border-gray-200">
                {layoutConfig?.logo_url && !logoError ? (
                  <img 
                    src={layoutConfig.logo_url} 
                    alt={layoutConfig.nome_empresa || "Logo da empresa"}
                    className="h-16 w-auto max-w-48 object-contain"
                    onError={(e) => {
                      console.error('❌ Erro ao carregar logo no login:', layoutConfig.logo_url);
                      setLogoError(true);
                    }}
                  />
                ) : (
                  <img 
                    src="https://i.postimg.cc/L5xrJKcL/logo-prospera.png"
                    alt="Prospera Consultoria"
                    className="h-16 w-auto max-w-48 object-contain"
                    onError={(e) => {
                      console.error('❌ Erro ao carregar logo padrão');
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-8 space-y-8">
            {/* Título do formulário */}
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Acesso ao Sistema
              </h2>
              <p className="text-gray-600 text-sm">
                Entre com suas credenciais para continuar
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Campo E-mail */}
              <div className="space-y-3">
                <Label htmlFor="email" className="text-gray-700 font-semibold text-sm">
                  E-mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Digite seu e-mail"
                  required
                  disabled={isLoading}
                  className="h-12 px-4 border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl text-gray-900 placeholder:text-gray-500"
                />
              </div>

              {/* Campo Senha */}
              <div className="space-y-3">
                <Label htmlFor="password" className="text-gray-700 font-semibold text-sm">
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Digite sua senha"
                    required
                    disabled={isLoading}
                    className="h-12 px-4 pr-12 border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl text-gray-900 placeholder:text-gray-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Link Esqueci minha senha */}
              <div className="text-right">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  Esqueci minha senha
                </button>
              </div>

              {/* Botão de acesso */}
              <Button
                type="submit"
                className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-3" />
                    ACESSAR SISTEMA
                    <ArrowRight className="w-5 h-5 ml-3" />
                  </>
                )}
              </Button>
            </form>

            {/* Informações adicionais */}
            <div className="pt-6 border-t border-gray-100">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-2">
                  Desenvolvido por Prospera Consultoria
                </p>
                <p className="text-xs text-gray-400">
                  © {new Date().getFullYear()} - Todos os direitos reservados
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
